import React from 'react';
import { Button } from '@/components/ui/button';
import { Printer, Download, Mail } from 'lucide-react';

/**
 * Harris Teeter Leave-Behind
 * 
 * Executive-ready 1-pager.
 * "Thought this was interesting" — zero extra explanation needed.
 */

const HT_RED = '#E31837';

const HarrisTeeterLeaveBehind = () => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Print-friendly styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-page { 
            padding: 0 !important; 
            max-width: 100% !important;
          }
          body { 
            font-size: 11pt !important;
            line-height: 1.4 !important;
          }
          h1 { font-size: 20pt !important; }
          h2 { font-size: 13pt !important; }
          h3 { font-size: 11pt !important; }
        }
      `}</style>

      {/* Action Bar - Hidden on Print */}
      <div className="no-print sticky top-0 bg-white border-b z-50">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
          <span className="text-sm text-gray-500">Harris Teeter • Executive Overview</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />
              Print / Save PDF
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.location.href = 'mailto:?subject=Harris%20Teeter%20Meal%20Planning%20Overview&body=Thought%20this%20was%20interesting%3A%0A%0A' + encodeURIComponent(window.location.href)}
            >
              <Mail className="h-4 w-4 mr-2" />
              Share
            </Button>
          </div>
        </div>
      </div>

      {/* Document Content */}
      <div className="print-page max-w-3xl mx-auto px-8 py-12 bg-white min-h-screen">
        
        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-6">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
              style={{ backgroundColor: HT_RED }}
            >
              HT
            </div>
            <span className="text-sm text-gray-400 uppercase tracking-wide">Internal Overview</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Dinner, Simplified
          </h1>
          <p className="text-lg text-gray-600">
            A Harris Teeter Planning Experience
          </p>
        </div>

        {/* Section: Problem */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-gray-900 mb-3 pb-2 border-b">
            The problem we&apos;re solving
          </h2>
          <p className="text-gray-700 mb-4">
            Many customers want healthy, home-cooked meals — but don&apos;t want:
          </p>
          <ul className="text-gray-700 space-y-1 ml-4">
            <li>• meal kits or subscriptions</li>
            <li>• pre-boxed ingredients</li>
            <li>• hours of weekly planning</li>
            <li>• endless recipe decisions</li>
          </ul>
          <p className="text-gray-700 mt-4">
            As a result, they either default to convenience foods or abandon planning altogether.
          </p>
        </section>

        {/* Section: Idea */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-gray-900 mb-3 pb-2 border-b">
            The idea
          </h2>
          <p className="text-gray-700 mb-4">
            A guided weekly meal planning experience that helps customers:
          </p>
          <ul className="text-gray-700 space-y-1 ml-4">
            <li>• choose how they want the week to feel</li>
            <li>• automatically generate a simple dinner plan</li>
            <li>• build a complete Harris Teeter cart in one tap</li>
          </ul>
          <p className="text-gray-700 mt-4 font-medium">
            No meal kits. No boxes. Just smarter use of Harris Teeter groceries.
          </p>
        </section>

        {/* Section: How It Works */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-gray-900 mb-3 pb-2 border-b">
            How it works (customer view)
          </h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">1. Choose a path, not recipes</h3>
              <p className="text-gray-700 mb-2">Customers select a simple goal:</p>
              <div className="flex flex-wrap gap-2 ml-4">
                <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">Weeknight Healthy</span>
                <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">Family Friendly</span>
                <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">High Protein</span>
                <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">Budget Friendly</span>
                <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">Low Prep</span>
              </div>
              <p className="text-gray-600 text-sm mt-2 ml-4">This removes decision fatigue immediately.</p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">2. Get a flexible weekly plan</h3>
              <p className="text-gray-700 mb-2">The system generates 5 dinners using:</p>
              <ul className="text-gray-700 space-y-1 ml-4 text-sm">
                <li>• familiar ingredients</li>
                <li>• overlapping items (less waste)</li>
                <li>• flexible swaps (protein, veg, carb, sauce)</li>
              </ul>
              <p className="text-gray-600 text-sm mt-2 ml-4">Meals are guidance — not rigid recipes.</p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">3. One-tap cart build</h3>
              <p className="text-gray-700 mb-2">Ingredients are consolidated into a realistic weekly cart using:</p>
              <ul className="text-gray-700 space-y-1 ml-4 text-sm">
                <li>• Harris Teeter inventory logic</li>
                <li>• store brand preference</li>
                <li>• organic or budget bias (optional)</li>
                <li>• pantry exclusions</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">4. Seamless checkout handoff</h3>
              <p className="text-gray-700">
                The cart flows into Harris Teeter&apos;s existing checkout and loyalty experience.
              </p>
            </div>
          </div>
        </section>

        {/* Section: What This Is Not */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-gray-900 mb-3 pb-2 border-b">
            What this is not
          </h2>
          <div className="grid grid-cols-2 gap-2 text-gray-700">
            <div className="flex items-center gap-2">
              <span className="text-red-500">✗</span>
              <span>Not a meal kit</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-red-500">✗</span>
              <span>Not a subscription</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-red-500">✗</span>
              <span>Not a recipe marketplace</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-red-500">✗</span>
              <span>Not replacing prepared foods</span>
            </div>
          </div>
          <p className="text-gray-600 text-sm mt-3">
            This complements existing offerings by solving weekly decision friction.
          </p>
        </section>

        {/* Section: Why It Matters */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-gray-900 mb-3 pb-2 border-b">
            Why this matters for Harris Teeter
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-sm uppercase tracking-wide">Customer value</h3>
              <ul className="text-gray-700 text-sm space-y-1">
                <li>• Less planning stress</li>
                <li>• More shopping confidence</li>
                <li>• Familiar ingredients they trust</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-sm uppercase tracking-wide">Business impact</h3>
              <ul className="text-gray-700 text-sm space-y-1">
                <li>• Higher weekly basket intent</li>
                <li>• More full-week shops</li>
                <li>• Stronger loyalty</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-sm uppercase tracking-wide">Brand-safe</h3>
              <ul className="text-gray-700 text-sm space-y-1">
                <li>• No health claims</li>
                <li>• No PII required</li>
                <li>• Fully white-labeled</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section: Built For HT */}
        <section className="mb-10 p-6 rounded-lg" style={{ backgroundColor: `${HT_RED}08` }}>
          <h2 className="text-lg font-semibold mb-3" style={{ color: HT_RED }}>
            Built for Harris Teeter
          </h2>
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
            <div className="flex items-center gap-2">
              <span style={{ color: HT_RED }}>✓</span>
              <span>White-label experience</span>
            </div>
            <div className="flex items-center gap-2">
              <span style={{ color: HT_RED }}>✓</span>
              <span>Store brand and promotion bias</span>
            </div>
            <div className="flex items-center gap-2">
              <span style={{ color: HT_RED }}>✓</span>
              <span>Inventory-aware logic</span>
            </div>
            <div className="flex items-center gap-2">
              <span style={{ color: HT_RED }}>✓</span>
              <span>Privacy-first analytics</span>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-4">
            This can live as a Harris Teeter feature — not a third-party app.
          </p>
        </section>

        {/* Summary */}
        <section className="mb-10 p-6 bg-gray-50 rounded-lg">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Summary</h2>
          <p className="text-gray-700">
            This helps customers feel like they&apos;ve planned the week — without meal kits, 
            subscriptions, or decision overload — using Harris Teeter groceries.
          </p>
        </section>

        {/* Footer */}
        <footer className="pt-6 border-t text-center">
          <p className="text-sm text-gray-400">
            Demo available: <span className="text-gray-600">/demo/harris-teeter</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Confidential • For internal discussion only
          </p>
        </footer>

      </div>
    </>
  );
};

export default HarrisTeeterLeaveBehind;
